import type { CmsUserDetailResponse } from '@akpl/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api';
import { useAuth } from '../auth/AuthProvider';

/** What the console prints when it cannot name the person. */
export const UNKNOWN_OPERATOR = '—';

/**
 * A human name for the operator behind a stored uid ("Último guardado … por …"). The API only
 * records the Firebase uid (`settings.updated_by`, the `home.publish` audit actor), and a raw
 * `oPCtdxvg…` means nothing to the person reading the header (UI review 2026-10-05).
 *
 * In order: the signed-in operator themself, then — for an admin, who may read `/cms/users/:id`
 * — the user's display name or e-mail, sharing the Users screen's cache. Anything else, a
 * missing uid or a lookup that failed, reads as a neutral dash; never the uid.
 */
export function useOperatorName(uid: string | null | undefined): string {
  const { account, me, role } = useAuth();
  const isSelf = !!uid && (uid === account?.uid || uid === me?.id);
  const lookup = !!uid && !isSelf && !uid.includes('@') && role === 'admin';

  const detail = useQuery<CmsUserDetailResponse>({
    queryKey: ['cms', 'user', uid],
    queryFn: () => api.cmsUser({ params: { id: uid! } }),
    enabled: lookup,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (!uid) return UNKNOWN_OPERATOR;
  // Some writers store the e-mail itself, which is already readable.
  if (uid.includes('@')) return uid;
  if (isSelf) {
    return (
      me?.displayName || me?.email || account?.displayName || account?.email || UNKNOWN_OPERATOR
    );
  }
  const user = lookup ? detail.data?.user : undefined;
  return user?.displayName || user?.email || UNKNOWN_OPERATOR;
}
